import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Button,
  ConfigProvider,
  DatePicker,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  TreeSelect,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { UserOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import moment from "moment";

import CustomPagination from "../../../../../../core/common/customPagination/CustomPagination";
import { humanReadableDate } from "../../../../../../helper/dateTimeHelper";
import ReminderDropdown from "../../../components/ReminderDropdown";
import {
  computeReminderBuckets,
  getLeadCity,
  getLeadState,
} from "../../helpers/leadsTransfer.helper";
import {
  LEAD_SOURCE_PARENTS,
  LEAD_SOURCE_TREE,
  ROW_LIMIT_DEFAULT,
} from "../../constants/leadsTransfer.constants";
import type { UserOption } from "../../types";

dayjs.extend(isBetween);
const { RangePicker } = DatePicker;

interface TransferLeadsModalProps {
  open: boolean;
  onClose: () => void;
  userOptions: UserOption[];
  fromUserId: number | null;
  setFromUserId: (id: number | null) => void;
  toUserId: number | null;
  setToUserId: (id: number | null) => void;
  baseLeads: any[];
  transferring: boolean;
  onTransfer: (selectedKeys: number[]) => void;
}

const userSelectFilter = (input: string, option?: any) =>
  (option?.label ?? "").toString().toLowerCase().includes(input.toLowerCase());

const TransferLeadsModal: React.FC<TransferLeadsModalProps> = ({
  open,
  onClose,
  userOptions,
  fromUserId,
  setFromUserId,
  toUserId,
  setToUserId,
  baseLeads,
  transferring,
  onTransfer,
}) => {
  const [pipelineFilter, setPipelineFilter] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null]>([null, null]);
  const [searchTerm, setSearchTerm] = useState("");
  const [cityFilter, setCityFilter] = useState<string | undefined>(undefined);
  const [stateFilter, setStateFilter] = useState<string | undefined>(undefined);
  const [leadSource, setLeadSource] = useState<string | undefined>(undefined);
  const [selectedLeads, setSelectedLeads] = useState<number[]>([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(ROW_LIMIT_DEFAULT);

  // Reminders are computed from the selected employee's own leads (fast, no API)
  const [followUpFilter, setFollowUpFilter] = useState("all");
  const [reminderCustomStart, setReminderCustomStart] = useState<moment.Moment | null>(null);
  const [reminderCustomEnd, setReminderCustomEnd] = useState<moment.Moment | null>(null);

  const buckets = useMemo(() => computeReminderBuckets(baseLeads), [baseLeads]);

  const resetFilters = () => {
    setPipelineFilter(null);
    setDateRange([null, null]);
    setSearchTerm("");
    setCityFilter(undefined);
    setStateFilter(undefined);
    setLeadSource(undefined);
    setFollowUpFilter("all");
    setReminderCustomStart(null);
    setReminderCustomEnd(null);
    setSelectedLeads([]);
    setPage(1);
  };

  // Reset everything when the source employee changes
  useEffect(() => {
    resetFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromUserId]);

  const pipelineOptions = useMemo(
    () =>
      Array.from(new Set(baseLeads.map((l) => l.pipeline_char).filter(Boolean))).map((p) => ({
        label: p,
        value: p,
      })),
    [baseLeads],
  );

  const stateOptions = useMemo(
    () =>
      Array.from(new Set(baseLeads.map(getLeadState).filter(Boolean))).map((s) => ({
        label: s as string,
        value: s as string,
      })),
    [baseLeads],
  );

  // Cities cascade from the selected state (all cities when no state chosen)
  const cityOptions = useMemo(() => {
    const source = stateFilter
      ? baseLeads.filter((l) => getLeadState(l) === stateFilter)
      : baseLeads;
    return Array.from(new Set(source.map(getLeadCity).filter(Boolean))).map((c) => ({
      label: c as string,
      value: c as string,
    }));
  }, [baseLeads, stateFilter]);

  // Reset the city when it no longer belongs to the selected state
  useEffect(() => {
    if (cityFilter && !cityOptions.some((o) => o.value === cityFilter)) {
      setCityFilter(undefined);
    }
  }, [cityOptions, cityFilter]);

  const filteredLeads = useMemo(() => {
    let list: any[];
    if (followUpFilter === "today") list = buckets.leads.today;
    else if (followUpFilter === "overdue") list = buckets.leads.overdue;
    else if (followUpFilter === "upcoming") list = buckets.leads.upcoming;
    else if (followUpFilter === "custom") {
      list = [...buckets.leads.today, ...buckets.leads.overdue, ...buckets.leads.upcoming];
      if (reminderCustomStart && reminderCustomEnd) {
        list = list.filter((lead) => {
          const m = moment(lead.follow_up_date).local();
          return (
            m.isSameOrAfter(reminderCustomStart, "day") &&
            m.isSameOrBefore(reminderCustomEnd, "day")
          );
        });
      }
    } else {
      list = baseLeads;
    }

    if (leadSource) {
      const isParent = LEAD_SOURCE_PARENTS.includes(leadSource);
      list = list.filter((lead) =>
        isParent ? lead.lead_source === leadSource : lead.lead_source_child === leadSource,
      );
    }
    if (pipelineFilter) {
      list = list.filter((lead) => lead.pipeline_char === pipelineFilter);
    }
    if (dateRange[0] && dateRange[1]) {
      list = list.filter((lead) =>
        dayjs(lead.createdAt, "M/D/YYYY, h:mm:ss A").isBetween(
          dateRange[0]!.startOf("day"),
          dateRange[1]!.endOf("day"),
          null,
          "[]",
        ),
      );
    }
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      list = list.filter(
        (lead) =>
          lead.LeadName?.toLowerCase().includes(s) || lead.Phone?.toLowerCase().includes(s),
      );
    }
    if (stateFilter) {
      list = list.filter((lead) => getLeadState(lead) === stateFilter);
    }
    if (cityFilter) {
      list = list.filter((lead) => getLeadCity(lead) === cityFilter);
    }
    return list;
  }, [
    baseLeads,
    buckets,
    followUpFilter,
    reminderCustomStart,
    reminderCustomEnd,
    leadSource,
    pipelineFilter,
    dateRange,
    searchTerm,
    cityFilter,
    stateFilter,
  ]);

  // Keep the selection within the currently visible/filtered set
  useEffect(() => {
    const keys = new Set(filteredLeads.map((l) => Number(l.key)));
    setSelectedLeads((prev) => prev.filter((k) => keys.has(k)));
  }, [filteredLeads]);

  const total = filteredLeads.length;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  useEffect(() => {
    setPage(1);
  }, [total, limit]);

  const pagedLeads = useMemo(
    () => filteredLeads.slice((page - 1) * limit, page * limit),
    [filteredLeads, page, limit],
  );

  const allFilteredSelected = total > 0 && selectedLeads.length >= total;

  const columns: ColumnsType<any> = [
    {
      title: "Sr No.",
      key: "srNo",
      width: 70,
      align: "center",
      render: (_, __, index) => (
        <span style={{ fontSize: 12, fontWeight: 500, color: "#6b7280" }}>
          {(page - 1) * limit + index + 1}
        </span>
      ),
    },
    {
      title: "Name",
      dataIndex: "LeadName",
      key: "LeadName",
      width: 180,
      ellipsis: true,
      render: (text: string) => (
        <span className="fw-medium" style={{ fontSize: 13 }}>
          {text || "—"}
        </span>
      ),
    },
    {
      title: "Phone",
      dataIndex: "Phone",
      key: "Phone",
      width: 140,
      render: (text: string) => <span style={{ fontSize: 13 }}>{text || "—"}</span>,
    },
    {
      title: "Pipeline",
      dataIndex: "pipeline_char",
      key: "pipeline_char",
      width: 150,
      ellipsis: true,
      render: (text: string) =>
        text ? (
          <Tag color="blue" style={{ fontSize: 11.5, margin: 0 }}>
            {text}
          </Tag>
        ) : (
          "—"
        ),
    },
    {
      title: "City",
      key: "city",
      width: 130,
      ellipsis: true,
      render: (_: any, record: any) => (
        <span style={{ fontSize: 13 }}>{getLeadCity(record) || "—"}</span>
      ),
    },
    {
      title: "State",
      key: "state",
      width: 130,
      ellipsis: true,
      render: (_: any, record: any) => (
        <span style={{ fontSize: 13 }}>{getLeadState(record) || "—"}</span>
      ),
    },
    {
      title: "Follow-up",
      dataIndex: "follow_up_date",
      key: "follow_up_date",
      width: 160,
      render: (value: string) =>
        value ? (
          <span style={{ fontSize: 12.5, color: "#475569" }}>{humanReadableDate(value, true)}</span>
        ) : (
          <span style={{ color: "#94a3b8" }}>—</span>
        ),
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 160,
      render: (value: string) =>
        value ? (
          <span style={{ fontSize: 12.5, color: "#475569" }}>{humanReadableDate(value, true)}</span>
        ) : (
          "—"
        ),
    },
  ];

  const footer = [
    <Button key="cancel" onClick={onClose} disabled={transferring}>
      Cancel
    </Button>,
    <Button
      key="submit"
      type="primary"
      loading={transferring}
      disabled={selectedLeads.length === 0}
      onClick={() => onTransfer(selectedLeads)}
    >
      <i className="ti ti-repeat me-1" />
      Transfer {selectedLeads.length > 0 ? `(${selectedLeads.length})` : ""}
    </Button>,
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={
        <span className="d-flex align-items-center">
          <i className="ti ti-repeat me-2 text-primary" />
          Transfer Leads
        </span>
      }
      width="90%"
      style={{ maxWidth: 1180 }}
      className="leads-transfer-modal"
      centered
      zIndex={1001}
      maskClosable={false}
      destroyOnClose
      afterClose={resetFilters}
      footer={footer}
    >
      {/* From / To selection */}
      <div className="row g-3 mb-3">
        <div className="col-md-6">
          <label className="form-label fw-semibold">Transfer From</label>
          <Select
            showSearch
            allowClear
            style={{ width: "100%" }}
            placeholder="Select user to transfer from"
            value={fromUserId ?? undefined}
            onChange={(v) => setFromUserId(v ?? null)}
            options={userOptions}
            optionFilterProp="label"
            filterOption={userSelectFilter}
            optionRender={(option) => (
              <Space>
                <Avatar size="small" src={(option.data as any)?.image || undefined} icon={<UserOutlined />} />
                <span>{option.label}</span>
              </Space>
            )}
          />
        </div>
        <div className="col-md-6">
          <label className="form-label fw-semibold">Transfer To</label>
          <Select
            showSearch
            allowClear
            style={{ width: "100%" }}
            placeholder="Select user to transfer to"
            value={toUserId ?? undefined}
            onChange={(v) => setToUserId(v ?? null)}
            options={userOptions}
            optionFilterProp="label"
            filterOption={userSelectFilter}
            optionRender={(option) => (
              <Space>
                <Avatar size="small" src={(option.data as any)?.image || undefined} icon={<UserOutlined />} />
                <span>{option.label}</span>
              </Space>
            )}
          />
        </div>
      </div>

      {!fromUserId ? (
        <div
          className="d-flex justify-content-center align-items-center bg-white"
          style={{ minHeight: 240, borderRadius: 12, border: "1px dashed #cbd5e1" }}
        >
          <div className="text-center">
            <i className="ti ti-user-search" style={{ fontSize: 40, color: "#cbd5e1" }} />
            <h6 className="mt-3 mb-1 fw-semibold">Select a "Transfer From" employee</h6>
            <p className="text-muted mb-0">Their leads (and reminders) will appear here.</p>
          </div>
        </div>
      ) : (
        <>
          <ConfigProvider theme={{ token: { controlHeight: 38 } }}>
            <div className="lt-modal-filterbar mb-3">
              <ReminderDropdown
                followUpFilter={followUpFilter}
                setFollowUpFilter={setFollowUpFilter}
                reminderLeadIds={buckets.ids}
                isReminderLoading={false}
                reminderCustomStart={reminderCustomStart}
                reminderCustomEnd={reminderCustomEnd}
                setReminderCustomStart={setReminderCustomStart}
                setReminderCustomEnd={setReminderCustomEnd}
                onOpen={() => { }}
              />
              <Select
                allowClear
                placeholder="Pipeline"
                style={{ width: 160 }}
                options={pipelineOptions}
                value={pipelineFilter ?? undefined}
                onChange={(v) => setPipelineFilter(v ?? null)}
              />
              <TreeSelect
                value={leadSource}
                showSearch
                treeNodeFilterProp="title"
                filterTreeNode={(input, node) =>
                  node?.title?.toString().toLowerCase().includes(input.toLowerCase()) || false
                }
                dropdownStyle={{ maxHeight: 400, overflow: "auto" }}
                treeData={LEAD_SOURCE_TREE}
                placeholder="Lead Source"
                allowClear
                treeDefaultExpandAll={false}
                onChange={(v) => setLeadSource(v)}
                style={{ width: 180 }}
              />
              <Select
                allowClear
                showSearch={false}
                placeholder="All States"
                style={{ width: 150 }}
                options={stateOptions}
                value={stateFilter}
                onChange={(v) => setStateFilter(v)}
              />
              <Select
                allowClear
                showSearch={false}
                placeholder="All Cities"
                style={{ width: 150 }}
                options={cityOptions}
                value={cityFilter}
                onChange={(v) => setCityFilter(v)}
              />
              <RangePicker
                value={dateRange}
                onChange={(dates) => setDateRange((dates as any) ?? [null, null])}
                allowClear
                style={{ width: 250 }}
                className="mb-0"
              />
              <Input
                placeholder="Search name or phone"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                allowClear
                autoComplete="off"
                style={{ width: 220 }}
                prefix={<i className="ti ti-search" />}
              />
            </div>
          </ConfigProvider>

          <div className="d-flex align-items-center justify-content-between mb-2 flex-wrap gap-2">
            <span className="text-muted" style={{ fontSize: 13 }}>
              Showing <strong>{total}</strong> lead(s) for the selected employee
            </span>
            <span style={{ fontSize: 13 }}>
              Selected: <strong>{selectedLeads.length}</strong>
              {total > 0 && !allFilteredSelected && (
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0 ms-2"
                  style={{ textDecoration: "none" }}
                  onClick={() => setSelectedLeads(filteredLeads.map((l) => Number(l.key)))}
                >
                  Select all {total}
                </button>
              )}
              {selectedLeads.length > 0 && (
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0 ms-2 text-danger"
                  style={{ textDecoration: "none" }}
                  onClick={() => setSelectedLeads([])}
                >
                  Clear
                </button>
              )}
            </span>
          </div>

          <div
            className="bg-white"
            style={{ borderRadius: 12, overflow: "hidden", border: "1px solid #e2e8f0" }}
          >
            <Table
              className="custom-data-table"
              columns={columns}
              dataSource={pagedLeads}
              rowKey="key"
              size="middle"
              pagination={false}
              scroll={{ x: 1100 }}
              rowSelection={{
                selectedRowKeys: selectedLeads,
                onChange: (keys) => setSelectedLeads(keys as number[]),
                preserveSelectedRowKeys: true,
              }}
              rowClassName={(_, index) =>
                index % 2 === 0 ? "data-table-row-light" : "data-table-row-dark"
              }
              locale={{ emptyText: "No leads match the selected filters." }}
            />
            <CustomPagination
              currentPage={page}
              totalPages={totalPages}
              total={total}
              limit={limit}
              hasNextPage={page < totalPages}
              hasPrevPage={page > 1}
              onPageChange={setPage}
              onLimitChange={(l) => {
                setLimit(l);
                setPage(1);
              }}
            />
          </div>
        </>
      )}
    </Modal>
  );
};

export default TransferLeadsModal;
